import { useCallback, useEffect, useRef, useState } from "react";
import {
  listTickets, getTicket, createTicket, replyTicket, setTicketStatus, attachmentURL, eventsURL,
  type SupportTicket,
} from "../services/support";
import { useLang } from "../hooks/useLang";
import Icon from "./icons";
import { useAuth } from "../auth/AuthContext";

// Müşteri perspektifi: son yanıtlayan tarafın panelinde "yanıtlandı", karşı
// tarafta "cevabınız bekleniyor". Müşteri viewer'ı için son yazan client ise
// "yanıtlandı"; destek (superadmin) ise müşteriden yanıt bekleniyor.
function statusInfo(tk: SupportTicket, t: (k: string) => string): { label: string; cls: string } {
  if (tk.status === "closed") return { label: t("support_status_closed"), cls: "off" };
  if (tk.last_author_role === "superadmin") return { label: t("support_status_awaiting"), cls: "warn" };
  return { label: t("support_status_answered"), cls: "ok" };
}
function prioLabel(t: (k: string) => string, p: string): string {
  return p === "high" ? t("support_priority_high") : p === "low" ? t("support_priority_low") : t("support_priority_normal");
}
function fmtDate(s: string): string {
  try { return new Date(s).toLocaleString(); } catch { return s; }
}

export default function SupportPanel() {
  const { t } = useLang();
  const { user } = useAuth();
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [view, setView] = useState<"list" | "new" | "detail">("list");
  const [active, setActive] = useState<SupportTicket | null>(null);
  const activeIdRef = useRef<number | null>(null);
  activeIdRef.current = active?.id ?? null;

  // new ticket form
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [priority, setPriority] = useState("normal");
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);

  // reply form
  const [reply, setReply] = useState("");
  const [replyFiles, setReplyFiles] = useState<File[]>([]);
  const [replying, setReplying] = useState(false);

  const loadList = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try { setTickets(await listTickets()); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setLoading(false); }
  }, []);

  const reloadActive = useCallback(async (id: number) => {
    try { setActive(await getTicket(id)); } catch { /* sessiz */ }
  }, []);

  useEffect(() => { void loadList(); }, [loadList]);

  // --- Canlı SSE: olay gelince listeyi (ve açık talebi) tazele ---
  useEffect(() => {
    const es = new EventSource(eventsURL());
    const onEvt = (ev: MessageEvent) => {
      void loadList();
      try {
        const data = JSON.parse(ev.data) as { ticket_id: number };
        if (activeIdRef.current && data.ticket_id === activeIdRef.current) void reloadActive(activeIdRef.current);
      } catch { /* ignore */ }
    };
    es.addEventListener("ticket_created", onEvt);
    es.addEventListener("message_added", onEvt);
    es.addEventListener("ticket_updated", onEvt);
    return () => es.close();
  }, [loadList, reloadActive]);

  const openDetail = useCallback(async (id: number) => {
    setErr(null);
    try { setActive(await getTicket(id)); setView("detail"); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  }, []);

  const onlyPdf = (list: FileList | null): File[] =>
    list ? Array.from(list).filter((f) => f.name.toLowerCase().endsWith(".pdf")).slice(0, 5) : [];

  const onCreate = useCallback(async () => {
    if (!subject.trim() || !body.trim()) return;
    setSubmitting(true);
    setErr(null);
    try {
      const created = await createTicket({ subject: subject.trim(), body: body.trim(), priority, files });
      setSubject(""); setBody(""); setPriority("normal"); setFiles([]);
      setActive(created); setView("detail");
      void loadList();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setSubmitting(false); }
  }, [subject, body, priority, files, loadList]);

  const onReply = useCallback(async () => {
    if (!active || !reply.trim()) return;
    setReplying(true);
    setErr(null);
    try {
      const updated = await replyTicket(active.id, reply.trim(), replyFiles);
      setActive(updated); setReply(""); setReplyFiles([]);
      void loadList();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setReplying(false); }
  }, [active, reply, replyFiles, loadList]);

  const onToggleClose = useCallback(async () => {
    if (!active) return;
    setErr(null);
    try {
      const updated = await setTicketStatus(active.id, active.status === "closed" ? "open" : "closed");
      setActive(updated);
      void loadList();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  }, [active, loadList]);

  // ---- List ----
  if (view === "list") {
    return (
      <div className="sup">
        <div className="sup-head">
          <div>
            <h2 className="sup-title">{t("support_title")}</h2>
            <p className="sup-sub">{t("support_intro")}</p>
          </div>
          <button className="sup-btn sup-btn-primary" onClick={() => setView("new")}>＋ {t("support_new_btn")}</button>
        </div>
        {err && <div className="error">{err}</div>}
        {loading && <div className="sup-empty">{t("support_loading")}</div>}
        {!loading && tickets.length === 0 && (
          <div className="sup-empty">
            <div className="sup-empty-ico"><Icon name="headset" size={40} /></div>
            <p>{t("support_empty")}</p>
          </div>
        )}
        <div className="sup-list">
          {tickets.map((tk) => {
            const si = statusInfo(tk, t);
            return (
            <button key={tk.id} className={"sup-card sup-card-" + si.cls} onClick={() => openDetail(tk.id)}>
              <div className="sup-card-main">
                <div className="sup-card-subject">{tk.subject}</div>
                <div className="sup-card-meta">
                  <span>#{tk.id}</span>
                  <span>· {prioLabel(t, tk.priority)}</span>
                  <span>· 💬 {tk.message_count ?? 0}</span>
                  <span>· {fmtDate(tk.updated_at)}</span>
                </div>
              </div>
              <span className={"badge " + si.cls}>{si.label}</span>
            </button>
            );
          })}
        </div>
      </div>
    );
  }

  // ---- New ----
  if (view === "new") {
    return (
      <div className="sup">
        <button className="sup-back" onClick={() => setView("list")}>{t("support_back")}</button>
        <h2 className="sup-title">{t("support_new_btn")}</h2>
        {err && <div className="error">{err}</div>}
        <div className="sup-form">
          <label className="sup-field"><span>{t("support_subject")}</span>
            <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </label>
          <label className="sup-field"><span>{t("support_priority")}</span>
            <select value={priority} onChange={(e) => setPriority(e.target.value)}>
              <option value="low">{t("support_priority_low")}</option>
              <option value="normal">{t("support_priority_normal")}</option>
              <option value="high">{t("support_priority_high")}</option>
            </select>
          </label>
          <label className="sup-field"><span>{t("support_message")}</span>
            <textarea rows={6} value={body} onChange={(e) => setBody(e.target.value)} />
          </label>
          <label className="sup-field"><span>{t("support_attach")}</span>
            <input type="file" accept="application/pdf,.pdf" multiple onChange={(e) => setFiles(onlyPdf(e.target.files))} />
          </label>
          {files.length > 0 && <div className="sup-files">{files.map((f) => <span key={f.name} className="sup-chip">📎 {f.name}</span>)}</div>}
          <small className="sup-hint">{t("support_only_pdf")}</small>
          <div className="sup-actions">
            <button className="sup-btn sup-btn-primary" disabled={submitting || !subject.trim() || !body.trim()} onClick={onCreate}>
              {submitting ? t("saving") : t("support_create_btn")}
            </button>
            <button className="sup-btn" disabled={submitting} onClick={() => setView("list")}>{t("support_cancel")}</button>
          </div>
        </div>
      </div>
    );
  }

  // ---- Detail ----
  if (!active) { setView("list"); return null; }
  const closed = active.status === "closed";
  return (
    <div className="sup sup-detail-view">
      <button className="sup-back" onClick={() => { setView("list"); setActive(null); }}>{t("support_back")}</button>
      <div className="sup-detail-head">
        <div>
          <div className="sup-detail-id">#{active.id}</div>
          <h2 className="sup-title">{active.subject}</h2>
          <div className="sup-card-meta">{prioLabel(t, active.priority)} · {fmtDate(active.created_at)}</div>
        </div>
        <div className="sup-detail-head-right">
          <span className={"badge " + statusInfo(active, t).cls}>{statusInfo(active, t).label}</span>
          <button className="sup-btn sup-btn-sm" onClick={onToggleClose}>
            {closed ? t("support_reopen_ticket") : t("support_close_ticket")}
          </button>
        </div>
      </div>
      {err && <div className="error">{err}</div>}

      <div className="sup-thread">
        {(active.messages ?? []).map((m) => {
          const admin = m.author_role === "superadmin";
          return (
            <div key={m.id} className={"sup-msg " + (admin ? "sup-msg-admin" : "sup-msg-client")}>
              <div className="sup-msg-head">
                <strong>{admin ? t("support_team") : (user?.id === m.author_id ? t("support_you") : (m.author_name || ""))}</strong>
                <span>{fmtDate(m.created_at)}</span>
              </div>
              <div className="sup-msg-body">{m.body}</div>
              {(m.attachments ?? []).length > 0 && (
                <div className="sup-files">
                  {m.attachments!.map((a) => (
                    <a key={a.id} href={attachmentURL(a.id)} target="_blank" rel="noreferrer" className="sup-chip sup-chip-link">
                      📎 {a.file_name} <em>({(a.size_bytes / 1024).toFixed(0)} KB)</em>
                    </a>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="sup-detail-foot">
        {closed ? (
          <div className="sup-hint sup-closed">{t("support_closed_note")}</div>
        ) : (
          <div className="sup-composer">
            <textarea rows={2} value={reply} placeholder={t("support_reply_placeholder")} onChange={(e) => setReply(e.target.value)} />
            <div className="sup-composer-bar">
              <label className="sup-attach-btn">
                📎 {t("support_attach")}
                <input type="file" accept="application/pdf,.pdf" multiple onChange={(e) => setReplyFiles(onlyPdf(e.target.files))} />
              </label>
              {replyFiles.length > 0 && <span className="sup-files">{replyFiles.map((f) => <span key={f.name} className="sup-chip">{f.name}</span>)}</span>}
              <button className="sup-btn sup-btn-primary" style={{ marginLeft: "auto" }} disabled={replying || !reply.trim()} onClick={onReply}>
                {replying ? t("saving") : t("support_send")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
